/**
 * 实体卡主界面
 * 支持多卡切换，根据卡片状态显示不同内容：
 * - 已激活：显示正常的卡片信息和操作
 * - 未激活：显示卡片状态信息（审核中、待激活等）及物流信息
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { createCancellablePost, isRequestCancelled, type CancellableRequest } from '@/utils/request'
import UserPreferences from '@/utils/userPreferences'
import CardSwiper, { type CardData, type CardSwiperRef } from '../components/card-swiper'
import CardView from '../components/card-view'
import { CardStatus, isIssuedStatus } from '../index.types'
import CardActions, { CardActionType } from '../components/card-actions'
import TransactionRecords from '../components/transaction-records'
import PendingCard from '../components/pending-card'
import type { CardListItem } from '@/hooks'
import { APIWeb } from '@/api'
import AcceleratePopup from '../components/accelerate-popup'
import KycFailed from '../components/kyc-failed'

// 实体卡数据接口（扩展 CardData）
export interface PhysicalCardData extends CardData {
    cardName?: string
    trackingNumber?: string
    /** 持卡人KYC ID */
    kyc_id?: number
    /** 内部标记：余额是否已通过详情接口更新 */
    _balanceUpdated?: boolean
}

// 物流信息
interface ShippingInfo {
    /** 物流公司 */
    courierName?: string
    /** 物流单号 */
    trackingNumber?: string
}

interface PhysicalCardProps {
    /** 实体卡列表（从父组件传入） */
    cards: CardListItem[]
}

const PhysicalCard: React.FC<PhysicalCardProps> = ({ cards }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const swiperRef = useRef<CardSwiperRef>(null)

    // 本地卡片数据，用于更新余额（与虚拟卡一致）
    const [localCards, setLocalCards] = useState<PhysicalCardData[]>(() => cards as PhysicalCardData[])
    const [currentIndex, setCurrentIndex] = useState(() => {
        const saved = UserPreferences.getCardActiveIndexPhysical()
        return saved >= 0 ? saved : 0
    })
    
    // 物流信息
    const [shippingInfo, setShippingInfo] = useState<ShippingInfo | null>(null)
    // 物流信息加载状态
    const [shippingInfoLoading, setShippingInfoLoading] = useState(false)
    // 查询kyc审核状态加载状态
    const [accelerating, setAccelerating] = useState(false)
    // 查询kyc审核状态成功弹窗
    const [acceleratePopupVisible, setAcceleratePopupVisible] = useState(false)

    // 当前卡片
    const currentCard = localCards[currentIndex] || null

    // 当外部 cards 变化时同步更新 localCards（保留已获取的余额）
    useEffect(() => {
        setLocalCards(prevLocalCards => {
            return (cards as PhysicalCardData[]).map(card => {
                // 如果本地已有该卡片的更新余额，保留它
                const existingCard = prevLocalCards.find(c => c.item_id === card.item_id)
                if (existingCard?.balance_limit?.balance !== card.balance_limit?.balance) {
                    // 如果本地余额与新数据不同，且本地有更新过的余额，保留本地的
                    if (existingCard?._balanceUpdated) {
                        return { ...card, balance_limit: existingCard.balance_limit, _balanceUpdated: true }
                    }
                }
                return card
            })
        })
    }, [cards])

    // 索引越界保底：卡注销导致列表变短时回到第 0 张
    useEffect(() => {
        if (localCards.length > 0 && currentIndex >= localCards.length) {
            setCurrentIndex(0)
            UserPreferences.setCardActiveIndexPhysical(0)
            swiperRef.current?.slideTo(0)
        }
    }, [localCards.length, currentIndex])

    // 用于存储当前请求的引用，以便取消
    const currentRequestRef = useRef<CancellableRequest<getVirtualCardDetailResponse> | null>(null)
    // 用于存储物流信息请求的引用
    const shippingInfoRequestRef = useRef<CancellableRequest<getCardShippingInfoResponse> | null>(null)

    // 当前卡片的 item_id、状态和是否已下卡（用于触发详情获取）
    const currentCardId = localCards[currentIndex]?.item_id
    const currentCardStatus = localCards[currentIndex]?.status
    // 实体卡根据 status 判断是否为已下卡状态（可以获取详情）
    const isCurrentCardActive = isIssuedStatus(currentCardStatus)

    // 切换卡片时获取最新余额（已激活卡片）
    useEffect(() => {
        // 实体卡：只有已激活的卡片才需要获取详情
        if (!isCurrentCardActive || !currentCardId) {
            return
        }

        // 取消上一个未完成的请求
        if (currentRequestRef.current) {
            currentRequestRef.current.cancel()
        }

        const itemId = currentCardId

        // 创建可取消的请求
        const request = createCancellablePost<getVirtualCardDetailResponse>(
            '/web/card/info',
            { item_id: Number(itemId), issued: true }
        )
        currentRequestRef.current = request

        request.promise
            .then((res) => {
                const balance = res?.card?.balance_limit?.balance
                if (balance !== undefined) {
                    // 更新对应卡片的余额
                    setLocalCards(prev => prev.map(c =>
                        c.item_id === itemId
                            ? { ...c, balance_limit: { ...c.balance_limit, balance }, _balanceUpdated: true }
                            : c
                    ))
                }
            })
            .catch((error: any) => {
                // 如果是取消请求，静默处理
                if (isRequestCancelled(error)) return
                console.error('获取实体卡余额失败:', error)
            })
            .finally(() => {
                // 清除引用
                if (currentRequestRef.current === request) {
                    currentRequestRef.current = null
                }
            })

        // 组件卸载时取消请求
        return () => {
            if (currentRequestRef.current) {
                currentRequestRef.current.cancel()
                currentRequestRef.current = null
            }
        }
    }, [currentIndex, currentCardId, isCurrentCardActive])

    // 判断当前卡片是否处于待处理状态
    const isPendingStatusForShipping = currentCardStatus === CardStatus.Submitted ||
        currentCardStatus === CardStatus.KycPending ||
        currentCardStatus === CardStatus.PendingActivation ||
        currentCardStatus === CardStatus.Binded ||
        currentCardStatus === CardStatus.KycPassed

    // 待处理状态时获取物流信息
    useEffect(() => {
        if (!isPendingStatusForShipping || !currentCardId) {
            setShippingInfo(null)
            setShippingInfoLoading(false)
            return
        }

        // 取消上一个未完成的请求
        if (shippingInfoRequestRef.current) {
            shippingInfoRequestRef.current.cancel()
        }

        const cardId = Number(currentCardId)

        // 开始加载
        setShippingInfoLoading(true)

        // 调用 getCardShippingInfoV2 获取物流信息
        const request = createCancellablePost<getCardShippingInfoResponse>(
            '/web/card/fetch_physical_card_shipping',
            { id: cardId }
        )
        shippingInfoRequestRef.current = request

        request.promise
            .then((res) => {
                if (res?.carrier || res?.tracking_no) {
                    setShippingInfo({
                        courierName: res.carrier,
                        trackingNumber: res.tracking_no,
                    })
                } else {
                    setShippingInfo(null)
                }
            })
            .catch((error: any) => {
                if (isRequestCancelled(error)) return
                console.error('获取物流信息失败:', error)
                setShippingInfo(null)
            })
            .finally(() => {
                if (shippingInfoRequestRef.current === request) {
                    shippingInfoRequestRef.current = null
                    setShippingInfoLoading(false)
                }
            })

        return () => {
            if (shippingInfoRequestRef.current) {
                shippingInfoRequestRef.current.cancel()
                shippingInfoRequestRef.current = null
            }
        }
    }, [currentIndex, currentCardId, isPendingStatusForShipping])

    // 获取卡片ID（兼容新旧字段）
    const getCardId = (card: PhysicalCardData | null): number => {
        if (!card) return 0
        return Number(card.item_id || card.id || 0)
    }

    // 获取掩码卡号
    const getMaskedPan = (card: PhysicalCardData | null) => {
        if (!card) return ''
        return card.masked_pan || ''
    }

    // 判断卡片是否已下卡
    const isCardIssued = (card: PhysicalCardData | null): boolean => {
        if (!card) return false
        return card.card_issued === true
    }

    // 判断卡片是否已冻结
    const checkCardFrozen = (card: PhysicalCardData | null): boolean => {
        if (!card) return false
        return card.status === CardStatus.Frozen
    }

    // 判断当前卡片是否为已下卡状态（根据 status 字段判断）
    const isCardActive = isIssuedStatus(currentCard?.status)

    // 当前卡片是否已下卡
    const isCurrentCardIssued = useMemo(() => {
        return isCardIssued(currentCard)
    }, [currentCard])

    // 当前卡片是否已冻结
    const isCurrentCardFrozen = useMemo(() => {
        return checkCardFrozen(currentCard)
    }, [currentCard])

    // 判断当前卡片是否处于待处理状态（显示 PendingCard）
    const isPendingStatus = isPendingStatusForShipping

    // 判断当前卡片是否显示激活按钮（KycPassed / Binded / PendingActivation）
    const showActivate = currentCard?.status === CardStatus.KycPassed ||
        currentCard?.status === CardStatus.Binded ||
        currentCard?.status === CardStatus.PendingActivation
    // 判断当前卡片是否显示查询kyc审核状态按钮（未下卡且 KycPending 状态）
    const showAccelerate = !currentCard?.card_issued && currentCard?.status === CardStatus.KycPending
    // 判断当前卡片是否 KYC 失败
    const isKycFailed = currentCard?.status === CardStatus.KycFailed
    // 是否可查询kyc审核状态（未下卡且 can_sync_kyc 为 true，与虚拟卡判断一致）
    const canAccelerate = useMemo(() => {
        return !!(currentCard as any)?.can_sync_kyc
    }, [currentCard])

    // 卡片切换回调
    const handleCardChange = useCallback((_card: CardData, index: number) => {
        setCurrentIndex(index)
        UserPreferences.setCardActiveIndexPhysical(index)
    }, [])

    // 激活卡片
    const handleActivate = useCallback(async () => {
        if (!currentCard) return
        navigate(`/card-v2/physical-card/activate-card?id=${getCardId(currentCard)}`, {
            state: {
                backgroundImage: currentCard.image,
                cardBin: (currentCard as any)?.bin,
                productId: (currentCard as any)?.card_product_id,
                kycId: (currentCard as any)?.kyc_id,
                applicationId: (currentCard as any)?.item_id,
                activeType: (currentCard as any)?.active_type,
            }
        })
    }, [currentCard, navigate])

    // 查询kyc审核状态（Submitted 状态）
    const handleAccelerateCard = useCallback(async () => {
        if (!currentCard) return

        if (!canAccelerate) {
            new Error('当前暂不可查询kyc审核状态')
            return
        }

        const anyCard = currentCard as any
        const { card_product_id, kyc_id } = anyCard || {}

        if (!card_product_id || !kyc_id) {
            new Error('缺少必要信息暂时无法查询kyc审核状态')
            return
        }

        try {
            setAccelerating(true)
            await APIWeb.accelerateCardV2({
                card_product_id: Number(card_product_id),
                kyc_id: Number(kyc_id)
            })
            setAcceleratePopupVisible(true)
        } catch (error) {
            console.error('实体卡查询kyc审核状态失败:', error)
        } finally {
            setAccelerating(false)
        }
    }, [currentCard, canAccelerate, t])

    // 重新填写（KYC失败时）- 跳转到KYC编辑页面（与虚拟卡逻辑一致，传递isReapply标志）
    const handleReapply = useCallback(() => {
        const kycId = currentCard?.kyc_id
        const applicationId = currentCard?.item_id
        if (!kycId) return
        navigate('/my/cardholder/edit', { state: { kycId, applicationId, isReapply: true } })
    }, [currentCard, navigate])

    // 获取当前卡片信息（用于传递给子页面）
    const getCurrentCardInfo = useCallback(() => {
        if (!currentCard) return {}
        return {
            maskedPan: currentCard.masked_pan,
            balance: currentCard.balance_limit?.balance ?? currentCard.available_balance,
            backgroundImage: currentCard.image,
            cardIssued: currentCard.card_issued,
            status: currentCard.status
        }
    }, [currentCard])

    // 操作按钮点击回调
    const handleActionClick = useCallback((actionType: CardActionType) => {
        if (!currentCard) return

        const cardId = getCardId(currentCard)
        const maskedPan = getMaskedPan(currentCard)
        const cardIssued = isCardIssued(currentCard)
        const cardInfo = getCurrentCardInfo()
        
        switch (actionType) {
            case CardActionType.Recharge:
                navigate(`/card-v2/recharge?id=${cardId}&cardNo=${maskedPan}`)
                break
            case CardActionType.Transfer:
                navigate(`/card-v2/card-withdraw?id=${cardId}&cardNo=${maskedPan}`, {
                    state: cardInfo
                })
                break
            case CardActionType.Info:
                navigate(`/card-v2/card-info?id=${cardId}&issued=${cardIssued}`)
                break
            case CardActionType.Manage:
                navigate(`/card-v2/card-manage?id=${cardId}&cardNo=${maskedPan}&frozen=${isCurrentCardFrozen}`, {
                    state: cardInfo
                })
                break
            default:
                break
        }
    }, [currentCard, navigate, isCurrentCardFrozen, getCurrentCardInfo])

    // 自定义卡片渲染（根据状态渲染不同样式）
    // 与虚拟卡一致，从本地卡列表获取信息（包含更新后的余额）
    // 自定义卡片渲染（统一使用 isIssuedStatus 判断）
    const renderCard = useCallback((card: CardData, index: number) => {
        // 使用 localCards 中的数据（包含更新后的余额）
        const physicalCard = (localCards[index] || card) as PhysicalCardData

        // 优先级：balance_limit.balance > available_balance > balance
        const displayBalance = physicalCard.balance_limit?.balance ?? physicalCard.available_balance ?? physicalCard.balance

        return (
            <CardView
                cardStatus={physicalCard.status}
                cardIssued={physicalCard.card_issued === true}
                maskedPan={physicalCard.masked_pan}
                balance={displayBalance}
                backgroundImage={physicalCard.image}
            />
        )
    }, [localCards])

    if (localCards.length === 0 || !currentCard) {
        return null
    }

    return (
        <div className="flex flex-col pb-10">
            {/* 卡片轮播 */}
            <CardSwiper
                ref={swiperRef}
                cards={localCards}
                onCardChange={handleCardChange}
                renderCard={renderCard}
                initialIndex={currentIndex}
            />

            {/* 根据当前卡片状态显示不同内容 */}
            {isCardActive && isCurrentCardIssued ? (
                // 已激活且已下卡：显示操作按钮和交易记录
                <>
                    <CardActions 
                        onActionClick={handleActionClick}
                        className="mt-4"
                    />

                    {/* 交易记录 */}    
                    <TransactionRecords
                        cardId={getCardId(currentCard)}
                        cardInfo={{
                            cardImage: currentCard.image,
                            cardNo: currentCard.masked_pan,
                            cardStatus: currentCard.status,
                            cardType: currentCard.form_factor
                        }}
                        className="mt-6"
                    />
                </>
            ) : isPendingStatus ? (
                // 待处理状态：显示 PendingCard
                <div className="mt-2">
                    <PendingCard
                        cardName={currentCard.cardName}
                        trackingNumber={shippingInfo?.trackingNumber}
                        courierName={shippingInfo?.courierName}
                        onActivate={showActivate ? handleActivate : undefined}
                        canActivate={showActivate}
                        onAccelerate={showAccelerate ? handleAccelerateCard : undefined}
                        canAccelerate={showAccelerate && canAccelerate}
                        accelerateLoading={showAccelerate && accelerating}
                        hideCardPreview={true}
                        loading={shippingInfoLoading}
                    />
                </div>
            ) : isKycFailed ? (
                <KycFailed onReapply={handleReapply} />
            ) : null}
            {/* 查询kyc审核状态成功弹窗 */}
            <AcceleratePopup
                visible={acceleratePopupVisible}
                onClose={() => setAcceleratePopupVisible(false)}
            />
        </div>
    )
}

export default PhysicalCard
